import { set, child, update, remove, onValue } from 'firebase/database'
import { proxy, useSnapshot } from 'valtio'

import * as firebaseConnection from '../firebaseConnection'
import {
  type PlaybackCommand,
  type PlaybackStatus,
} from '../model/playback-model'

const ROOT_NODES = {
  command: 'videoPlaybackCommand',
  status: 'videoPlaybackStatus',
}

const playbackState = proxy<{
  command: PlaybackCommand | null
  status: PlaybackStatus | null
  isCommandReady: boolean
  isStatusReady: boolean
}>({
  command: null,
  status: null,
  isCommandReady: false,
  isStatusReady: false,
})

// Subscribe
firebaseConnection.runWhenReady((connection) => {
  // Adds a Firebase listener to track the playback the producers want.
  onValue(child(connection.room, ROOT_NODES.command), (snapshot) => {
    playbackState.command = snapshot.val()
    playbackState.isCommandReady = true
  })

  // Adds a Firebase listener to track the playback as headless reports it.
  onValue(child(connection.room, ROOT_NODES.status), (snapshot) => {
    playbackState.status = snapshot.val()
    playbackState.isStatusReady = true
  })
})

// Producer commands

/**
 * Writes a whole new command, replacing the current one.
 * @param command - The command, without its identifier.
 * @returns A promise that resolves when the command is written.
 */
const sendCommand = async (command: Omit<PlaybackCommand, 'id'>) => {
  const connection = await firebaseConnection.get()

  return set(child(connection.room, ROOT_NODES.command), {
    ...command,
    id: crypto.randomUUID(),
  }).catch((err: unknown) => {
    connection.logError(
      'SEND_VIDEO_PLAYBACK_COMMAND',
      'Failed to execute the last video playback command',
      err
    )
  })
}

/**
 * Re-sends the current command with some fields changed. Does nothing when no
 *  playback is going on.
 * @param patch - The fields to change.
 * @returns A promise that resolves when the command is written.
 */
const resendCommand = async (patch: Partial<PlaybackCommand>) => {
  const { command } = playbackState

  if (!command) return undefined

  return sendCommand({ ...command, position: null, ...patch })
}

/**
 * Starts playing a video. Without an explicit position the video resumes from
 *  the progress stored on its tray card.
 * @param video - The video to play.
 * @param options - Where the playback starts from and the card it belongs to.
 * @returns A promise that resolves when the command is written.
 */
const startPlayback = async (
  video: { photoId: string; url: string },
  { position, cardId }: { position?: number; cardId?: string } = {}
) =>
  sendCommand({
    playing: true,
    photoId: video.photoId,
    url: video.url,
    cardId: cardId ?? null,
    position: position ?? null,
  })

/**
 * Starts the current video over from the beginning.
 * @returns A promise that resolves when the command is written.
 */
const replayPlayback = async () => resendCommand({ playing: true, position: 0 })

/**
 * Pauses the current playback.
 * @returns A promise that resolves when the command is written.
 */
const pausePlayback = async () => resendCommand({ playing: false })

/**
 * Resumes the current playback.
 * @returns A promise that resolves when the command is written.
 */
const resumePlayback = async () => resendCommand({ playing: true })

/**
 * Pauses a playing playback, and resumes a paused one.
 * @returns A promise that resolves when the command is written.
 */
const togglePlayback = async () => {
  const { command } = playbackState

  if (!command) return undefined

  return resendCommand({ playing: !command.playing })
}

/**
 * Seeks the current playback to a position.
 * @param position - The position to seek to.
 * @returns A promise that resolves when the command is written.
 */
const seekPlayback = async (position: number) => resendCommand({ position })

/**
 * Stops the playback entirely.
 * @returns A promise that resolves when the command is removed.
 */
const stopPlayback = async () => {
  if (!playbackState.command) return undefined

  const connection = await firebaseConnection.get()

  return remove(child(connection.room, ROOT_NODES.command)).catch(
    (err: unknown) => {
      connection.logError(
        'STOP_VIDEO_PLAYBACK',
        'Failed to stop video playback',
        err
      )
    }
  )
}

// Headless status reporting

/**
 * Sets the whole playback status.
 * @param status - The status of the playback.
 * @returns A promise that resolves when the status is written.
 */
const setPlaybackStatus = async (status: PlaybackStatus) => {
  const connection = await firebaseConnection.get()

  return set(child(connection.room, ROOT_NODES.status), status).catch(
    (err: unknown) => {
      connection.logError(
        'SET_VIDEO_PLAYBACK_STATUS',
        'Failed to report the video playback status',
        err
      )
    }
  )
}

/**
 * Updates part of the playback status.
 * @param status - The status fields to update.
 * @returns A promise that resolves when the status is written.
 */
const updatePlaybackStatus = async (status: Partial<PlaybackStatus>) => {
  const connection = await firebaseConnection.get()

  return update(child(connection.room, ROOT_NODES.status), status).catch(
    (err: unknown) => {
      connection.logError(
        'UPDATE_VIDEO_PLAYBACK_STATUS',
        'Failed to report the video playback status',
        err
      )
    }
  )
}

/**
 * Removes the playback status.
 * @returns A promise that resolves when the status is removed.
 */
const removePlaybackStatus = async () => {
  const connection = await firebaseConnection.get()

  return remove(child(connection.room, ROOT_NODES.status)).catch(
    (err: unknown) => {
      connection.logError(
        'REMOVE_VIDEO_PLAYBACK_STATUS',
        'Failed to clear the video playback status',
        err
      )
    }
  )
}

const usePlaybackCommand = () => useSnapshot(playbackState).command
const usePlaybackStatus = () => useSnapshot(playbackState).status
const useIsPlaybackCommandReady = () =>
  useSnapshot(playbackState).isCommandReady
const useIsPlaybackStatusReady = () => useSnapshot(playbackState).isStatusReady

export {
  playbackState,
  usePlaybackCommand,
  usePlaybackStatus,
  useIsPlaybackCommandReady,
  useIsPlaybackStatusReady,
  startPlayback,
  replayPlayback,
  pausePlayback,
  resumePlayback,
  togglePlayback,
  seekPlayback,
  stopPlayback,
  setPlaybackStatus,
  updatePlaybackStatus,
  removePlaybackStatus,
}
